import { useState, useEffect, useCallback, useRef } from 'react';
import { isElectron } from '../utils/electron';

interface UsePushToTalkOptions {
  enabled: boolean;
  hotkey: string;
  onStart: () => void;
  onStop: () => void;
}

export function usePushToTalk({ enabled, hotkey, onStart, onStop }: UsePushToTalkOptions) {
  const [isPressed, setIsPressed] = useState(false);
  const isPressedRef = useRef(false);
  const onStartRef = useRef(onStart);
  const onStopRef = useRef(onStop);
  const enabledRef = useRef(enabled);

  // Keep refs up to date
  useEffect(() => {
    onStartRef.current = onStart;
    onStopRef.current = onStop;
  }, [onStart, onStop]);

  useEffect(() => {
    enabledRef.current = enabled;
  }, [enabled]);

  useEffect(() => {
    if (enabled || !isPressedRef.current) {
      return;
    }

    isPressedRef.current = false;
    setIsPressed(false);
    onStopRef.current();
  }, [enabled]);

  // Handle PTT state change (works for both browser and Electron)
  // Uses refs so the function identity is stable and won't cause listener accumulation
  const handlePTTStateChange = useCallback((pressed: boolean) => {
    if (!enabledRef.current) return;

    if (pressed && !isPressedRef.current) {
      isPressedRef.current = true;
      setIsPressed(true);
      onStartRef.current();
    } else if (!pressed && isPressedRef.current) {
      isPressedRef.current = false;
      setIsPressed(false);
      onStopRef.current();
    }
  }, []);

  const normalizeKey = useCallback((value: string) => {
    const lower = value.toLowerCase();
    if (lower === ' ' || lower === 'spacebar' || lower === 'space') {
      return 'space';
    }
    return lower;
  }, []);

  const handleKeyDown = useCallback((event: KeyboardEvent) => {
    if (!enabled) return;

    // Match the hotkey
    const key = event.code || event.key;
    if (normalizeKey(key) !== normalizeKey(hotkey)) return;

    // Prevent repeat events when key is held
    if (isPressedRef.current) return;

    // Don't trigger if user is typing in an input
    const target = event.target as HTMLElement;
    if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable) {
      return;
    }

    const activeElement = document.activeElement;
    if (activeElement instanceof HTMLElement && (activeElement.tagName === 'BUTTON' || activeElement.tagName === 'A')) {
      activeElement.blur();
    }

    event.preventDefault();
    event.stopPropagation();
    handlePTTStateChange(true);
  }, [enabled, handlePTTStateChange, hotkey, normalizeKey]);

  const handleKeyUp = useCallback((event: KeyboardEvent) => {
    if (!enabled) return;

    const key = event.code || event.key;
    if (normalizeKey(key) !== normalizeKey(hotkey)) return;

    if (!isPressedRef.current) return;

    event.preventDefault();
    event.stopPropagation();
    handlePTTStateChange(false);
  }, [enabled, handlePTTStateChange, hotkey, normalizeKey]);

  // Handle window blur (user switches away while holding key) - browser only
  const handleBlur = useCallback(() => {
    // In Electron, we have global shortcuts so blur doesn't matter
    if (isElectron()) return;

    if (isPressedRef.current) {
      isPressedRef.current = false;
      setIsPressed(false);
      onStopRef.current();
    }
  }, []);

  // Always sync hotkey to Electron main process when it changes (regardless of enabled state)
  // This ensures the global keyboard hook uses the correct key even when not live
  useEffect(() => {
    if (isElectron() && window.electronAPI) {
      window.electronAPI.onPTTHotkeyChanged(hotkey);
    }
  }, [hotkey]);

  // Set up event listeners
  useEffect(() => {
    if (!enabled) return;

    // If running in Electron, use global shortcuts via IPC
    if (isElectron() && window.electronAPI) {
      // Listen for global PTT state changes (stable callback avoids listener accumulation)
      window.electronAPI.onPTTStateChanged(handlePTTStateChange);

      // Safety net: sync PTT state on window focus to recover from lost IPC events
      const handleFocus = () => {
        void window.electronAPI?.getPTTState().then((actualPressed) => {
          if (isPressedRef.current && !actualPressed) {
            isPressedRef.current = false;
            setIsPressed(false);
            onStopRef.current();
          }
        });
      };
      window.addEventListener('focus', handleFocus);

      return () => {
        window.electronAPI?.removePTTStateListener();
        window.removeEventListener('focus', handleFocus);
      };
    }

    // Browser fallback - only works when page is focused
    window.addEventListener('keydown', handleKeyDown, { capture: true });
    window.addEventListener('keyup', handleKeyUp, { capture: true });
    window.addEventListener('blur', handleBlur);
    document.addEventListener('visibilitychange', handleBlur);
    window.addEventListener('pagehide', handleBlur);

    return () => {
      window.removeEventListener('keydown', handleKeyDown, { capture: true });
      window.removeEventListener('keyup', handleKeyUp, { capture: true });
      window.removeEventListener('blur', handleBlur);
      document.removeEventListener('visibilitychange', handleBlur);
      window.removeEventListener('pagehide', handleBlur);
    };
  }, [enabled, handleKeyDown, handleKeyUp, handleBlur, handlePTTStateChange]);

  return { isPressed };
}
